import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import type React from "react";
import { useEffect, useState } from "react";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import type { DeleteEntityTextElements } from "~/components/DeleteEntityView";
import DeleteEntityView from "~/components/DeleteEntityView";
import Label from "~/components/formElements/Label";
import {
	deleteVacationLedgerEntry,
} from "~/modules/absence/api/vacationLedgerEntry/vacationLedgerEntryApiDispatchers.ts";
import {
	STAFF_MEMBERS_VACATION_LEDGER_ENTRIES_QUERY_KEY,
	STAFF_MEMBERS_VACATION_LEDGER_REPORT_QUERY_KEY,
} from "~/modules/absence/api/vacationLedgerEntry/vacationLedgerEntryQueries.ts";
import type {
	VacationLedgerEntrySummaryType,
} from "~/modules/absence/api/vacationLedgerEntry/vacationLedgerEntryTypes.ts";
import { formatVacationDays } from "~/modules/absence/utils/vacationLedgerFormUtils.ts";

type DeleteVacationLedgerEntryModalProps = {
	vacationLedgerEntryData: VacationLedgerEntrySummaryType | null;
	isOpen: boolean;
	onCloseClick: () => void;
};

const DeleteVacationLedgerEntryModal: React.FunctionComponent<DeleteVacationLedgerEntryModalProps> = ({
	vacationLedgerEntryData,
	onCloseClick,
	isOpen,
}) => {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const queryClient = useQueryClient();

	useEffect(() => {
		if (isOpen) {
			setError("");
		}
	}, [isOpen]);

	const handleConfirmationClick = async () => {
		if (!vacationLedgerEntryData) return;
		try {
			setBusy(true);
			await deleteVacationLedgerEntry({
				staffMemberId: vacationLedgerEntryData.staffMemberId,
				vacationLedgerEntryId: vacationLedgerEntryData.id,
			});
			await queryClient.invalidateQueries({ queryKey: STAFF_MEMBERS_VACATION_LEDGER_ENTRIES_QUERY_KEY(vacationLedgerEntryData.staffMemberId) });
			await queryClient.invalidateQueries({ queryKey: STAFF_MEMBERS_VACATION_LEDGER_REPORT_QUERY_KEY(vacationLedgerEntryData.staffMemberId) });
			onCloseClick();
		} catch (error) {
			const errorData = handleApiError(error);
			if (errorData.type === ApiResponseErrorType.VALIDATION) {
				if (
					errorData.messages.vacationLedgerEntryId?.find(
						(validationError) => validationError.rule === "vacationLedgerEntryIsDeletableOrUpdatableRule",
					)
				) {
					setError("Der Eintrag kann nicht gelöscht werden.");
				}
			} else {
				setError("Ein unbekannter Fehler ist aufgetreten.");
			}
		}
		setBusy(false);
	};

	const texts: DeleteEntityTextElements = {
		headerText: `Urlaubskontoeintrag löschen`,
		bodyText: `Möchtest Du diesen Eintrag wirklich löschen?`,
		deleteButtonText: `Eintrag löschen`,
	};

	return (
		<DeleteEntityView
			error={error}
			texts={texts}
			isOpen={isOpen}
			onCloseClick={onCloseClick}
			busy={busy}
			handleConfirmationClick={handleConfirmationClick}
		>
			{!!vacationLedgerEntryData && <div className="mb-4 flex flex-col gap-y-2">
				<div>
					<Label>Typ</Label>
					<span className="text-base font-bold">{vacationLedgerEntryData.type}</span>
				</div>
				<div>
					<Label>Kommentar</Label>
					<span className="text-base font-bold">{vacationLedgerEntryData.comment || "k.A."}</span>
				</div>
				<div>
					<Label>Gültig ab</Label>
					<span className="text-base font-bold">{vacationLedgerEntryData.validFrom}</span>
				</div>
				<div>
					<Label>Urlaubstage</Label>
					<span className={clsx("text-base font-bold", vacationLedgerEntryData.vacationDays.isNegative() ? "text-red-500" : "text-green-500")}>{formatVacationDays(vacationLedgerEntryData.vacationDays)}</span>
				</div>
			</div>}

		</DeleteEntityView>
	);
};

export default DeleteVacationLedgerEntryModal;
